import { FALLBACK_TAG_TERMS } from '@/lib/item-filter-fallbacks';
import type { AiSearchPlan, ControlledTagTerm, ItemDetail, ItemListItem } from '@/types';

type SearchableItem = Pick<ItemListItem, 'primaryRole' | 'tags'> | Pick<ItemDetail, 'primaryRole' | 'tags'>;

export interface AiSearchOverviewContext {
  summary: string;
  adjacentSummary: string | null;
  requiredTerms: ControlledTagTerm[];
  preferredTerms: ControlledTagTerm[];
  excludedTerms: ControlledTagTerm[];
  relatedRoleTerms: ControlledTagTerm[];
}

export interface ItemAiSearchContext {
  summary: string;
  adjacentSummary: string | null;
  matchedRequiredTerms: ControlledTagTerm[];
  matchedPreferredTerms: ControlledTagTerm[];
  matchedRelatedRoleTerms: ControlledTagTerm[];
  matchedTerms: ControlledTagTerm[];
  relatedRoleTerms: ControlledTagTerm[];
}

const fallbackTagTermLookup = new Map<string, ControlledTagTerm>(
  FALLBACK_TAG_TERMS.map((term) => [term.slug, {
    slug: term.slug,
    name: term.name,
    group: term.group,
    description: term.description,
    sortOrder: term.sortOrder,
    active: term.active,
  }]),
);


const TAG_GROUP_LABELS: Record<string, string> = {
  role: 'role',
  context: 'context',
  interaction: 'interaction',
  purpose: 'purpose',
  tone: 'tone',
  'industry-intent': 'industry fit',
  layout: 'layout',
  'visual-style': 'visual style',
  'responsive-behavior': 'responsive behavior',
};

const TAG_GROUP_ORDER = [
  'role',
  'context',
  'interaction',
  'purpose',
  'tone',
  'industry-intent',
  'layout',
  'visual-style',
  'responsive-behavior',
];

function getGroupLabel(group: string): string {
  return TAG_GROUP_LABELS[group] ?? slugToReadableLabel(group).toLowerCase();
}

function groupTerms(terms: ControlledTagTerm[]): Array<{ group: string; label: string; terms: ControlledTagTerm[] }> {
  const groups = new Map<string, ControlledTagTerm[]>();

  terms.forEach((term) => {
    const group = term.group || 'other';
    groups.set(group, [...(groups.get(group) ?? []), term]);
  });

  return Array.from(groups.entries())
    .sort(([left], [right]) => {
      const leftIndex = TAG_GROUP_ORDER.indexOf(left);
      const rightIndex = TAG_GROUP_ORDER.indexOf(right);
      const normalizedLeft = leftIndex === -1 ? TAG_GROUP_ORDER.length : leftIndex;
      const normalizedRight = rightIndex === -1 ? TAG_GROUP_ORDER.length : rightIndex;

      if (normalizedLeft !== normalizedRight) {
        return normalizedLeft - normalizedRight;
      }

      return left.localeCompare(right);
    })
    .map(([group, groupedTerms]) => ({
      group,
      label: getGroupLabel(group),
      terms: groupedTerms,
    }));
}

function buildGroupedCueList(terms: ControlledTagTerm[], maxGroups = 3, maxTermsPerGroup = 2): string {
  const grouped = groupTerms(terms).slice(0, maxGroups);

  return buildHumanList(
    grouped.map(({ label, terms: groupedTerms }) => {
      const termLabels = groupedTerms.slice(0, maxTermsPerGroup).map((term) => term.name);
      return `${label} ${buildHumanList(termLabels)}`;
    }),
  );
}

function slugToReadableLabel(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildHumanList(parts: string[]): string {
  const filtered = parts.filter((part) => part.trim().length > 0);

  if (filtered.length === 0) {
    return '';
  }

  if (filtered.length === 1) {
    return filtered[0] ?? '';
  }

  if (filtered.length === 2) {
    return `${filtered[0]} and ${filtered[1]}`;
  }

  return `${filtered.slice(0, -1).join(', ')}, and ${filtered[filtered.length - 1]}`;
}

function capitalizeSentence(value: string): string {
  if (value.length === 0) {
    return value;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function ensureControlledTerm(slug: string): ControlledTagTerm {
  return fallbackTagTermLookup.get(slug) ?? {
    slug,
    name: slugToReadableLabel(slug),
    group: 'unknown',
    description: '',
    sortOrder: 0,
    active: true,
  };
}

function uniqueTerms(terms: ControlledTagTerm[]): ControlledTagTerm[] {
  const seen = new Set<string>();

  return terms.filter((term) => {
    if (seen.has(term.slug)) {
      return false;
    }

    seen.add(term.slug);
    return true;
  });
}

function buildItemTermLookup(item?: SearchableItem): Map<string, ControlledTagTerm> {
  const lookup = new Map<string, ControlledTagTerm>();

  if (item?.primaryRole) {
    lookup.set(item.primaryRole.slug, item.primaryRole);
  }

  item?.tags.forEach((tag) => {
    lookup.set(tag.slug, tag);
  });

  return lookup;
}

function resolveTerms(slugs: string[], itemLookup?: Map<string, ControlledTagTerm>): ControlledTagTerm[] {
  return uniqueTerms(
    slugs
      .map((slug) => itemLookup?.get(slug) ?? ensureControlledTerm(slug)),
  );
}

function matchPlanTerms(
  slugs: string[],
  itemLookup: Map<string, ControlledTagTerm>,
): ControlledTagTerm[] {
  return uniqueTerms(
    slugs
      .filter((slug) => itemLookup.has(slug))
      .map((slug) => itemLookup.get(slug))
      .filter((term): term is ControlledTagTerm => Boolean(term)),
  );
}

export function getAiSearchOverviewContext(plan: AiSearchPlan): AiSearchOverviewContext {
  const requiredTerms = resolveTerms(plan.requiredTags);
  const preferredTerms = resolveTerms(plan.preferredTags);
  const excludedTerms = resolveTerms(plan.excludedTags);
  const relatedRoleTerms = resolveTerms(plan.relatedRoles);
  const summaryParts: string[] = [];

  if (requiredTerms.length > 0) {
    summaryParts.push(`looking for ${buildGroupedCueList(requiredTerms, 2, 2)}`);
  }

  if (preferredTerms.length > 0) {
    summaryParts.push(`preferring ${buildGroupedCueList(preferredTerms, 3, 2)} cues`);
  }

  if (excludedTerms.length > 0) {
    summaryParts.push(`avoiding ${buildGroupedCueList(excludedTerms, 2, 2)}`);
  }

  const summary = summaryParts.length > 0
    ? `${capitalizeSentence(buildHumanList(summaryParts))}.`
    : 'Showing AI recommendations for this prompt.';
  const adjacentSummary = relatedRoleTerms.length > 0
    ? `You may also want ${buildHumanList(relatedRoleTerms.map((term) => term.name))} sections nearby.`
    : null;

  return {
    summary,
    adjacentSummary,
    requiredTerms,
    preferredTerms,
    excludedTerms,
    relatedRoleTerms,
  };
}

export function getItemAiSearchContext(item: SearchableItem, plan: AiSearchPlan): ItemAiSearchContext {
  const itemLookup = buildItemTermLookup(item);
  const matchedRequiredTerms = matchPlanTerms(plan.requiredTags, itemLookup);
  const matchedPreferredTerms = matchPlanTerms(plan.preferredTags, itemLookup);
  const matchedRelatedRoleTerms = matchPlanTerms(plan.relatedRoles, itemLookup);
  const matchedTerms = uniqueTerms([
    ...matchedRequiredTerms,
    ...matchedPreferredTerms,
    ...matchedRelatedRoleTerms,
  ]);
  const primaryRoleSlug = item.primaryRole?.slug ?? null;
  const relatedRoleTerms = resolveTerms(plan.relatedRoles)
    .filter((term) => term.slug !== primaryRoleSlug)
    .slice(0, 4);

  let summary = 'Recommended for this AI search prompt.';

  if (matchedRequiredTerms.length > 0 && matchedPreferredTerms.length > 0) {
    summary = `Matches ${buildGroupedCueList(matchedRequiredTerms, 2, 2)} with ${buildGroupedCueList(matchedPreferredTerms, 3, 2)} cues.`;
  } else if (matchedRequiredTerms.length > 0) {
    summary = `Matches the requested ${buildGroupedCueList(matchedRequiredTerms, 2, 2)} cue.`;
  } else if (matchedPreferredTerms.length > 0) {
    summary = `Recommended for its ${buildGroupedCueList(matchedPreferredTerms, 3, 2)} cues.`;
  } else if (matchedRelatedRoleTerms.length > 0) {
    summary = `Recommended as a ${buildHumanList(matchedRelatedRoleTerms.map((term) => term.name))} section for this prompt.`;
  } else if (item.primaryRole) {
    summary = `Recommended as a ${item.primaryRole.name.toLowerCase()} pattern for this prompt.`;
  }

  const adjacentSummary = relatedRoleTerms.length > 0
    ? `Also consider ${buildHumanList(relatedRoleTerms.map((term) => term.name))}.`
    : null;

  return {
    summary,
    adjacentSummary,
    matchedRequiredTerms,
    matchedPreferredTerms,
    matchedRelatedRoleTerms,
    matchedTerms,
    relatedRoleTerms,
  };
}
